// G360_K470 - sort, filter, column choice and CSV export (the shared DataTable).
// G360_K462 - a failed read is said, not shown as an empty list or a zero; a refused change is said, not shown as done.
// Admin - Org requests approval queue. Staff approve -> org created + requester = owner.
import { useEffect, useState } from 'react';
import { Inbox, Check, X, Building2 } from 'lucide-react';
import { listOrgRequests, approveOrgRequest, denyOrgRequest } from '../lib/adminOrgApi.js';
import { EmptyState } from '@govern360/shared/components/ui.jsx';
import { DataTable } from '@govern360/shared/components/datatable.jsx';

const STATUS_PILL = { pending: 'p-warn', approved: 'p-succ', denied: 'p-gray' };

export default function OrgRequestsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [loadErr, setLoadErr] = useState('');

  async function refresh() {
    setLoading(true);
    const { data, error } = await listOrgRequests();
    setLoadErr(error ? (error.message || 'the read failed') : '');
    setRows(error ? [] : data);
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function approve(r) {
    if (!confirm(`Approve "${r.org_name}" for ${r.email}? This creates the org and makes them owner.`)) return;
    setBusy(r.id);
    const { error } = await approveOrgRequest(r.id);
    setBusy(null);
    if (error) { alert(error.message); return; }
    refresh();
  }
  async function deny(r) {
    if (!confirm(`Deny "${r.org_name}"?`)) return;
    setBusy(r.id);
    const { error } = await denyOrgRequest(r.id);
    setBusy(null);
    if (error) { alert(error.message); return; }
    refresh();
  }

  const pending = rows.filter((r) => r.status === 'pending');
  const STATUS_RANK = { pending: 0, approved: 1, denied: 2 };
  const COLS = [
    { key: 'org_name', label: 'Organization', render: (r) => <><Building2 size={14} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--g-text-2)' }} /><strong>{r.org_name}</strong></> },
    { key: 'email', label: 'Requested by', render: (r) => <span style={{ fontSize: 12 }}>{r.email}</span> },
    { key: 'note', label: 'Note', render: (r) => <span style={{ color: 'var(--g-text-2)', fontSize: 12 }}>{r.note || '-'}</span> },
    { key: 'created_at', label: 'When', value: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : null),
      sort: (r) => (r.created_at ? new Date(r.created_at).getTime() : null), csv: (r) => r.created_at || '' },
    { key: 'status', label: 'Status', sort: (r) => (r.status in STATUS_RANK ? STATUS_RANK[r.status] : 9),
      render: (r) => <span className={`pill ${STATUS_PILL[r.status] || 'p-gray'}`}>{r.status}</span> },
  ];

  return (
    <div>
      <div className="ph">
        <div>
          <h1 className="ph-h1">Org requests</h1>
          <p className="ph-sub">Approve new-organization requests. Approving creates the org and makes the requester its owner.</p>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="banner admin">
          <Inbox size={18} />
          <span><strong>{pending.length}</strong> request{pending.length > 1 ? 's' : ''} awaiting your decision.</span>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--g-text-2)' }}>Loading&hellip;</div>
      ) : loadErr ? (
        <div className="card g360-admin-err" style={{ padding: 24, borderLeft: '3px solid var(--g-danger)' }}>
          <p style={{ color: 'var(--g-danger)', fontSize: 14, margin: '0 0 8px' }}>Organization requests could not be read</p>
          <p style={{ fontSize: 13, color: 'var(--g-text-2)', margin: 0 }}>{loadErr}. This is not the same as there being none.</p>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Inbox} title="No org requests" hint="When a user requests a new organization from the console, it appears here for approval." />
      ) : (
        <DataTable rows={rows} columns={COLS} storageKey="g360.admin.orgreq.hiddenCols" name="org-requests"
          placeholder="Filter requests..." rowKey={(r) => r.id}
          actions={(r) => (r.status === 'pending' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn admin-primary" style={{ height: 28, padding: '0 10px', fontSize: 11 }} onClick={() => approve(r)} disabled={busy === r.id}><Check size={14} />Approve</button>
              <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11 }} onClick={() => deny(r)} disabled={busy === r.id}><X size={14} />Deny</button>
            </div>
          ) : <span style={{ color: 'var(--g-text-3)', fontSize: 12 }}>-</span>)} />
      )}
    </div>
  );
}
